import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { Download, FileJson, FileText, Calendar, Image, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { PrivacyNotice } from '../components/PrivacyNotice';
import AccountLayout from '../components/AccountLayout';

export default function DataExportPage() {
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const [loading, setLoading] = useState(false);
  const [exportType, setExportType] = useState<string | null>(null);

  const [selectedData, setSelectedData] = useState({
    profile: true,
    appointments: true,
    documents: true,
    activityLogs: false,
    preferences: false,
  });

  const handleToggle = (field: keyof typeof selectedData) => {
    setSelectedData({
      ...selectedData,
      [field]: !selectedData[field],
    });
  };

  const handleExport = async (format: 'json' | 'csv') => {
    setLoading(true);
    setExportType(format);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Create mock data
      const exportData: any = {};

      if (selectedData.profile) {
        exportData.profile = {
          firstName: user?.firstName,
          lastName: user?.lastName,
          email: user?.email,
          phone: user?.phone,
          emailVerified: user?.emailVerified,
          createdAt: user?.createdAt,
        };
      }

      if (selectedData.appointments) {
        exportData.appointments = [
          {
            id: 'apt_1',
            service: 'Custom Tattoo',
            date: '2025-01-20',
            time: '14:00',
            status: 'scheduled',
            price: 200,
          },
          // Add more mock appointments
        ];
      }

      if (selectedData.documents) {
        exportData.documents = [
          {
            id: 'doc_1',
            name: 'Consent Form',
            type: 'consent',
            uploadedAt: '2025-01-10',
            status: 'approved',
          },
          // Add more mock documents
        ];
      }

      if (selectedData.activityLogs) {
        exportData.activityLogs = [
          {
            action: 'LOGIN',
            timestamp: new Date().toISOString(),
            ipAddress: '192.168.1.1',
            success: true,
          },
          // Add more mock activity logs
        ];
      }

      if (selectedData.preferences) {
        exportData.preferences = {
          language: 'en',
          emailNotifications: true,
          smsNotifications: false,
        };
      }

      // Create download
      if (format === 'json') {
        const dataStr = JSON.stringify(exportData, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `digital-tattoo-data-${new Date().toISOString().split('T')[0]}.json`;
        link.click();
        URL.revokeObjectURL(url);
      } else if (format === 'csv') {
        // Simple CSV export (would need proper CSV generation in production)
        let csvContent = 'Category,Data\n';
        Object.entries(exportData).forEach(([key, value]) => {
          csvContent += `${key},"${JSON.stringify(value)}"\n`;
        });
        const dataBlob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `digital-tattoo-data-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to export data:', error);
    } finally {
      setLoading(false);
      setExportType(null);
    }
  };

  return (
    <AccountLayout>
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-display font-bold text-textPrimary mb-2">
            {t('auth.account.dataExport.title')}
          </h1>
          <p className="text-textSecondary">
            {t('auth.account.dataExport.subtitle')}
          </p>
        </div>

        {/* GDPR Info */}
        <PrivacyNotice variant="dataExport" className="mb-6" />

        {/* Select Data to Export */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 mb-6">
          <h2 className="text-xl font-semibold text-textPrimary mb-4">
            {t('auth.account.dataExport.selectData')}
          </h2>
          <p className="text-sm text-textSecondary mb-6">
            {t('auth.account.dataExport.selectDataDesc')}
          </p>

          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                  <FileText className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <p className="font-medium text-textPrimary">
                    {t('auth.account.dataExport.categories.profile')}
                  </p>
                  <p className="text-xs text-textSecondary">
                    {t('auth.account.dataExport.categories.profileDesc')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleToggle('profile')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  selectedData.profile ? 'bg-primary-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    selectedData.profile ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg">
                  <Calendar className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-textPrimary">
                    {t('auth.account.dataExport.categories.appointments')}
                  </p>
                  <p className="text-xs text-textSecondary">
                    {t('auth.account.dataExport.categories.appointmentsDesc')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleToggle('appointments')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  selectedData.appointments ? 'bg-primary-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    selectedData.appointments ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-orange-100 rounded-lg">
                  <Image className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="font-medium text-textPrimary">
                    {t('auth.account.dataExport.categories.documents')}
                  </p>
                  <p className="text-xs text-textSecondary">
                    {t('auth.account.dataExport.categories.documentsDesc')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleToggle('documents')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  selectedData.documents ? 'bg-primary-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    selectedData.documents ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-textPrimary">
                    {t('auth.account.dataExport.categories.activityLogs')}
                  </p>
                  <p className="text-xs text-textSecondary">
                    {t('auth.account.dataExport.categories.activityLogsDesc')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleToggle('activityLogs')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  selectedData.activityLogs ? 'bg-primary-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    selectedData.activityLogs ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between py-3">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-indigo-100 rounded-lg">
                  <FileText className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="font-medium text-textPrimary">
                    {t('auth.account.dataExport.categories.preferences')}
                  </p>
                  <p className="text-xs text-textSecondary">
                    {t('auth.account.dataExport.categories.preferencesDesc')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleToggle('preferences')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  selectedData.preferences ? 'bg-primary-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    selectedData.preferences ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Export Format */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 mb-6">
          <h2 className="text-xl font-semibold text-textPrimary mb-4">
            {t('auth.account.dataExport.format')}
          </h2>
          <p className="text-sm text-textSecondary mb-6">
            {t('auth.account.dataExport.formatDesc')}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              onClick={() => handleExport('json')}
              disabled={loading}
              className="flex items-center justify-between p-4 border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-12 h-12 bg-blue-100 group-hover:bg-blue-200 rounded-lg transition-colors">
                  <FileJson className="w-6 h-6 text-blue-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-textPrimary">JSON</p>
                  <p className="text-xs text-textSecondary">
                    {t('auth.account.dataExport.jsonDesc')}
                  </p>
                </div>
              </div>
              {loading && exportType === 'json' ? (
                <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
              ) : (
                <Download className="w-5 h-5 text-gray-400 group-hover:text-primary-500 transition-colors" />
              )}
            </button>

            <button
              onClick={() => handleExport('csv')}
              disabled={loading}
              className="flex items-center justify-between p-4 border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-12 h-12 bg-green-100 group-hover:bg-green-200 rounded-lg transition-colors">
                  <FileText className="w-6 h-6 text-green-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-textPrimary">CSV</p>
                  <p className="text-xs text-textSecondary">
                    {t('auth.account.dataExport.csvDesc')}
                  </p>
                </div>
              </div>
              {loading && exportType === 'csv' ? (
                <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
              ) : (
                <Download className="w-5 h-5 text-gray-400 group-hover:text-primary-500 transition-colors" />
              )}
            </button>
          </div>
        </div>

        {/* Additional Info */}
        <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6 border border-green-200">
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-textPrimary mb-2">
                {t('auth.account.dataExport.info.title')}
              </h3>
              <ul className="space-y-2 text-sm text-textSecondary">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{t('auth.account.dataExport.info.item1')}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{t('auth.account.dataExport.info.item2')}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{t('auth.account.dataExport.info.item3')}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{t('auth.account.dataExport.info.item4')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </AccountLayout>
  );
}

